"use client";

import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import {
  LuBan,
  LuCircleCheck,
  LuCircleX,
  LuClock,
  LuInfo,
  LuShield,
  LuTriangleAlert,
} from "react-icons/lu";

import { ButtonProps, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const AlertDialog = AlertDialogPrimitive.Root;
const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

export const alertDialogVariants = cva(
  "fixed left-4 right-4 top-[50%] z-50 grid max-w-lg mx-auto translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg",
  {
    variants: {
      variant: {
        default: "bg-white border-border",
        destructive:
          "border-red-200 bg-de-4 dark:border-red-800 dark:bg-red-950/50",
        warning:
          "border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/50",
        success:
          "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/50",
        info: "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/50",
        forbidden:
          "border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/50",
        security:
          "border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/50",
        pending:
          "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/50",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export type AlertDialogVariant = VariantProps<
  typeof alertDialogVariants
>["variant"];

export interface AlertDialogContentProps
  extends
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>,
    VariantProps<typeof alertDialogVariants> {}

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  AlertDialogContentProps
>(({ className, variant, ...props }, ref) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(alertDialogVariants({ variant }), className)}
      {...props}
    />
  </AlertDialogPortal>
));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

export const alertDialogHeaderVariants = cva(
  "flex flex-col space-y-2 text-center sm:text-left",
  {
    variants: {
      variant: {
        default: "",
        destructive: "text-de+2 dark:text-red-100",
        warning: "text-yellow-900 dark:text-yellow-100",
        success: "text-green-900 dark:text-green-100",
        info: "text-blue-900 dark:text-blue-100",
        forbidden: "text-orange-900 dark:text-orange-100",
        security: "text-purple-900 dark:text-purple-100",
        pending: "text-gray-900 dark:text-gray-100",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export type AlertDialogHeaderVariant = VariantProps<
  typeof alertDialogHeaderVariants
>["variant"];

export interface AlertDialogHeaderProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertDialogHeaderVariants> {}

const AlertDialogHeader = React.forwardRef<
  HTMLDivElement,
  AlertDialogHeaderProps
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(alertDialogHeaderVariants({ variant }), className)}
    {...props}
  />
));
AlertDialogHeader.displayName = "AlertDialogHeader";

const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-row justify-end space-x-2", className)}
    {...props}
  />
);
AlertDialogFooter.displayName = "AlertDialogFooter";

export const alertDialogTitleVariants = cva(
  "text-lg font-semibold flex items-center gap-2",
  {
    variants: {
      variant: {
        default: "",
        destructive: "text-de+2 dark:text-red-100",
        warning: "text-yellow-900 dark:text-yellow-100",
        success: "text-green-900 dark:text-green-100",
        info: "text-blue-900 dark:text-blue-100",
        forbidden: "text-orange-900 dark:text-orange-100",
        security: "text-purple-900 dark:text-purple-100",
        pending: "text-gray-900 dark:text-gray-100",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export type AlertDialogTitleVariant = VariantProps<
  typeof alertDialogTitleVariants
>["variant"];

export interface AlertDialogTitleProps
  extends
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>,
    VariantProps<typeof alertDialogTitleVariants> {
  showIcon?: boolean;
}

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  AlertDialogTitleProps
>(({ className, variant, showIcon = true, children, ...props }, ref) => {
  const getIcon = () => {
    if (!showIcon) return null;

    const iconProps = { className: "h-5 w-5 flex-shrink-0" };

    switch (variant) {
      case "destructive":
        return <LuCircleX {...iconProps} />;
      case "warning":
        return <LuTriangleAlert {...iconProps} />;
      case "success":
        return <LuCircleCheck {...iconProps} />;
      case "info":
        return <LuInfo {...iconProps} />;
      case "forbidden":
        return <LuBan {...iconProps} />;
      case "security":
        return <LuShield {...iconProps} />;
      case "pending":
        return <LuClock {...iconProps} />;
      default:
        return null;
    }
  };

  return (
    <AlertDialogPrimitive.Title
      ref={ref}
      className={cn(alertDialogTitleVariants({ variant }), className)}
      {...props}
    >
      {getIcon()}
      {children}
    </AlertDialogPrimitive.Title>
  );
});
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

export const alertDialogDescriptionVariants = cva("text-sm", {
  variants: {
    variant: {
      default: "text-muted-foreground",
      destructive: "text-red-700 dark:text-red-300",
      warning: "text-yellow-700 dark:text-yellow-300",
      success: "text-green-700 dark:text-green-300",
      info: "text-blue-700 dark:text-blue-300",
      forbidden: "text-orange-700 dark:text-orange-300",
      security: "text-purple-700 dark:text-purple-300",
      pending: "text-gray-700 dark:text-gray-300",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export type AlertDialogDescriptionVariant = VariantProps<
  typeof alertDialogDescriptionVariants
>["variant"];

export interface AlertDialogDescriptionProps
  extends
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>,
    VariantProps<typeof alertDialogDescriptionVariants> {}

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  AlertDialogDescriptionProps
>(({ className, variant, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn(alertDialogDescriptionVariants({ variant }), className)}
    {...props}
  />
));
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName;

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> &
    Pick<ButtonProps, "variant">
>(({ className, variant, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={cn(buttonVariants({ variant }), className, "min-w-20")}
    {...props}
  />
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(buttonVariants({ variant: "white" }), className)}
    {...props}
  />
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
};

// import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
// import * as React from "react";
// import { cva, type VariantProps } from "class-variance-authority";
// import { buttonVariants, ButtonProps } from "@/components/ui/button";
// import { cn } from "@/lib/utils";

// const AlertDialog = AlertDialogPrimitive.Root;

// const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

// const AlertDialogPortal = AlertDialogPrimitive.Portal;

// const AlertDialogOverlay = React.forwardRef<
//   React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
//   React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
// >(({ className, ...props }, ref) => (
//   <AlertDialogPrimitive.Overlay
//     className={cn(
//       "fixed inset-0 z-50 bg-black/65 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
//       className
//     )}
//     {...props}
//     ref={ref}
//   />
// ));
// AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

// const alertDialogVariants = cva(
//   "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
//   {
//     variants: {
//       variant: {
//         default: "border-border",
//         destructive:
//           "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/50",
//         warning:
//           "border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/50",
//         success:
//           "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/50",
//         info: "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/50",
//         forbidden:
//           "border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/50",
//         security:
//           "border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/50",
//         pending:
//           "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/50",
//       },
//     },
//     defaultVariants: {
//       variant: "default",
//     },
//   }
// );

// interface AlertDialogContentProps
//   extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>,
//     VariantProps<typeof alertDialogVariants> {}

// const AlertDialogContent = React.forwardRef<
//   React.ElementRef<typeof AlertDialogPrimitive.Content>,
//   AlertDialogContentProps
// >(({ className, variant, ...props }, ref) => (
//   <AlertDialogPortal>
//     <AlertDialogOverlay />
//     <AlertDialogPrimitive.Content
//       ref={ref}
//       className={cn(alertDialogVariants({ variant }), className)}
//       {...props}
//     />
//   </AlertDialogPortal>
// ));
// AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

// const AlertDialogHeader = ({
//   className,
//   ...props
// }: React.HTMLAttributes<HTMLDivElement>) => (
//   <div
//     className={cn(
//       "flex flex-col space-y-2 text-center sm:text-left",
//       className
//     )}
//     {...props}
//   />
// );
// AlertDialogHeader.displayName = "AlertDialogHeader";

// const AlertDialogFooter = ({
//   className,
//   ...props
// }: React.HTMLAttributes<HTMLDivElement>) => (
//   <div
//     className={cn(
//       "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
//       className
//     )}
//     {...props}
//   />
// );
// AlertDialogFooter.displayName = "AlertDialogFooter";

// const AlertDialogTitle = React.forwardRef<
//   React.ElementRef<typeof AlertDialogPrimitive.Title>,
//   React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
// >(({ className, ...props }, ref) => (
//   <AlertDialogPrimitive.Title
//     ref={ref}
//     className={cn("text-lg font-semibold", className)}
//     {...props}
//   />
// ));
// AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

// const AlertDialogDescription = React.forwardRef<
//   React.ElementRef<typeof AlertDialogPrimitive.Description>,
//   React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
// >(({ className, ...props }, ref) => (
//   <AlertDialogPrimitive.Description
//     ref={ref}
//     className={cn("text-sm text-muted-foreground", className)}
//     {...props}
//   />
// ));
// AlertDialogDescription.displayName =
//   AlertDialogPrimitive.Description.displayName;

// const AlertDialogAction = React.forwardRef<
//   React.ElementRef<typeof AlertDialogPrimitive.Action>,
//   React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> &
//     ButtonProps
// >(({ className, variant, ...props }, ref) => (
//   <AlertDialogPrimitive.Action
//     ref={ref}
//     className={cn(buttonVariants({ variant }), className, "min-w-20")}
//     {...props}
//   />
// ));
// AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

// const AlertDialogCancel = React.forwardRef<
//   React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
//   React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
// >(({ className, ...props }, ref) => (
//   <AlertDialogPrimitive.Cancel
//     ref={ref}
//     className={cn(
//       buttonVariants({ variant: "outline" }),
//       "mt-2 sm:mt-0",
//       className
//     )}
//     {...props}
//   />
// ));
// AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

// export {
//   AlertDialog,
//   AlertDialogAction,
//   AlertDialogCancel,
//   AlertDialogContent,
//   AlertDialogDescription,
//   AlertDialogFooter,
//   AlertDialogHeader,
//   AlertDialogOverlay,
//   AlertDialogPortal,
//   AlertDialogTitle,
//   AlertDialogTrigger,
// };
